import { type ApiResponse, ApiFactory } from '@/utils/api'
import { StoreEnums } from '@/constants/enums/api/store.enums'
import type {
  UpdateStoreTimeZoneRequest,
  StoreTimeZoneModifyRecord,
  GetStoreTimeZoneResponse
} from '@/models/types/settlementTimezone.types'

/**
 * 店家結帳時區設定 API Service
 *
 * 提供更新店家時區設定和取得歷史記錄的 API 整合。
 */
export class SettlementTimezoneService extends ApiFactory {
  constructor() {
    super('')
  }

  /**
   * 更新店家結帳時區和時間設定
   *
   * @param params - 包含 timeZoneOffset 和 settingHour 的請求參數
   * @returns API 回應，包含 isSuccess, resultCode, message 欄位
   *
   * @example
   * ```typescript
   * const response = await settlementTimezoneService.updateStoreTimeZone({
   *   timeZoneOffset: 8,
   *   settingHour: [{ hour: 1 }]
   * })
   * ```
   */
  updateStoreTimeZone = async (params: UpdateStoreTimeZoneRequest): Promise<ApiResponse> => {
    return await this.put<UpdateStoreTimeZoneRequest, ApiResponse>(
      StoreEnums.updateStoreTimeZone,
      params
    )
  }

  /**
   * 取得店家結帳時區修改記錄
   *
   * 當店家尚無任何記錄時，API 回傳 result: null 及 resultCode: "B06"（非失敗狀態），
   * 前端應將 null 轉換為空陣列處理。
   *
   * @returns 歷史記錄陣列（或 null 表示尚無記錄），每個元素包含 timeZoneID, settingHour, createDate
   *
   * @example
   * ```typescript
   * const response = await settlementTimezoneService.getStoreTimeZoneModifyRecord()
   * const records = response.result ?? []
   * ```
   */
  getStoreTimeZoneModifyRecord = async (): Promise<
    ApiResponse<StoreTimeZoneModifyRecord[] | null>
  > => {
    return await this.get<unknown, ApiResponse<StoreTimeZoneModifyRecord[] | null>>(
      StoreEnums.getStoreTimeZoneModifyRecord
    )
  }

  /**
   * 取得店家當前結帳時區和時間設定
   *
   * 用於 UpdateStoreModal 打開時初始化選擇器顯示值。
   * 注意：回應的 settingHour 陣列中屬性名為 `settingHour`（非 `hour`）。
   *
   * @returns 包含 timeZone（IANA string）和 settingHour 陣列的回應
   *
   * @example
   * ```typescript
   * const res = await settlementTimezoneService.getStoreTimeZone()
   * // res.result.timeZone = "Asia/Taipei"
   * // res.result.settingHour[0].settingHour = 0
   * ```
   */
  getStoreTimeZone = async (): Promise<ApiResponse<GetStoreTimeZoneResponse>> => {
    return await this.get<unknown, ApiResponse<GetStoreTimeZoneResponse>>(
      StoreEnums.getStoreTimeZone
    )
  }
}
